import type { UploadMeta, UploadResult } from "./tus-upload-service";
import { uploadRecording, UploadPauser } from "./tus-upload-service";
import { UploadState } from "./constants";

export interface UploadCallbacks {
  onProgress?: (percent: number, bytes: number, total: number) => void;
  forceRestart?: boolean;
}

export interface UploadManagerConfig {
  /** Minimum duration for upload progress display in milliseconds. Defaults to 0 (no smoothing) */
  minUploadDurationMs?: number;
  /** Progress update interval for smooth animation in milliseconds. Defaults to 50ms */
  progressUpdateIntervalMs?: number;
}

/**
 * Manages the lifecycle of a resumable file upload with pause/resume capabilities.
 */
export interface UploadManager {
  start(blob: Blob | null, meta: UploadMeta, cb?: UploadCallbacks): Promise<UploadResult>;
  cancel(): void;
  pause(): void;
  resume(): void;
}

/**
 * Default implementation of UploadManager that handles resumable uploads
 * with chunk-level pause/resume capabilities and optional progress smoothing.
 */
export class DefaultUploadManager implements UploadManager {
  private cancelled = false;
  private stage: UploadState = UploadState.Idle;
  private controller: AbortController | null = null;
  private pauser: UploadPauser | null = null;
  private smoothingTimer: NodeJS.Timeout | null = null;
  private smoothingCompletionPromise: Promise<void> | null = null;
  private resolveSmoothingCompletion: (() => void) | null = null;
  private smoothingMarkRealComplete: () => void = () => {};

  constructor(private config: UploadManagerConfig = {}) {}

  /**
   * Starts a resumable upload with the given blob and metadata.
   * Returns early if the upload has been cancelled.
   */
  async start(blob: Blob | null, meta: UploadMeta, cb?: UploadCallbacks): Promise<UploadResult> {
    if (this.cancelled) {
      return { success: false, error: "Upload cancelled" } as const;
    }

    this.stage = UploadState.Starting;
    this.cancelled = false;
    this.controller = new AbortController();
    this.pauser = new UploadPauser();
    this.clearSmoothingTimer();
    this.smoothingCompletionPromise = null;
    this.resolveSmoothingCompletion = null;
    this.smoothingMarkRealComplete = () => {};

    // Create progress smoother if minimum duration is configured
    const progressCallback = this.createProgressSmoother(cb?.onProgress);

    const result = await uploadRecording(blob, meta, {
      onProgress: (percent, loaded, total) => {
        this.stage = UploadState.InProgress;
        progressCallback(percent, loaded, total);
      },
      forceRestart: cb?.forceRestart,
      signal: this.controller.signal,
      pauser: this.pauser,
    });

    // For cases where the uploader did not emit a final 100% progress
    // (e.g., fallback multipart), explicitly mark real completion so
    // the smoother can finish waiting on the time schedule.
    if (result.success && !this.cancelled) {
      this.smoothingMarkRealComplete();
    }

    // If upload succeeded and smoothing is enforcing a minimum duration,
    // wait until the schedule has fully completed before reporting success.
    if (result.success && this.smoothingCompletionPromise && !this.cancelled) {
      try {
        await this.smoothingCompletionPromise;
      } catch {
        // Ignore
      }
    }

    this.stage = result.success ? UploadState.Succeeded : UploadState.Failed;
    return result;
  }

  /**
   * Cancels the upload by aborting the HTTP request and marking as cancelled.
   * Once cancelled, the upload cannot be resumed.
   */
  cancel(): void {
    this.cancelled = true;

    try {
      this.controller?.abort();
    } catch {
      // Ignore abort errors - controller may already be aborted
    }

    // Clear any active progress smoothing timers
    this.clearSmoothingTimer();

    // Unblock any waiters on smoothing completion
    {
      const resolveFn: (() => void) | null = this.resolveSmoothingCompletion;
      if (resolveFn) resolveFn();
    }
    this.resolveSmoothingCompletion = null;
    this.smoothingCompletionPromise = null;

    this.stage = UploadState.Cancelled;
  }

  /**
   * Pauses the upload at the next chunk boundary.
   * Upload can be resumed with resume().
   */
  pause(): void {
    this.pauser?.pause();
  }

  /**
   * Resumes a paused upload from the next chunk boundary.
   */
  resume(): void {
    this.pauser?.resume();
  }

  /**
   * Creates a progress callback wrapper that smooths rapid progress updates
   * to ensure a minimum display duration for better UX.
   */
  private createProgressSmoother(
    originalCallback?: (percent: number, bytes: number, total: number) => void
  ): (percent: number, bytes: number, total: number) => void {
    if (!originalCallback || !this.config.minUploadDurationMs) {
      // No smoothing needed - return original callback or noop
      return originalCallback ?? (() => {});
    }

    const uploadStartTime = Date.now();
    const minDuration = this.config.minUploadDurationMs!;
    const updateInterval = this.config.progressUpdateIntervalMs ?? 50;

    let realPercent = 0;
    let displayedPercent = 0;
    let lastLoaded = 0;
    let lastTotal = 0;

    // Set up a completion promise for external awaiting in start()
    this.smoothingCompletionPromise = new Promise<void>(resolve => {
      this.resolveSmoothingCompletion = resolve;
    });

    const computeScheduleCap = (): number => {
      const elapsed = Date.now() - uploadStartTime;
      const fraction = Math.min(elapsed / minDuration, 1);
      return Math.floor(fraction * 100);
    };

    const tick = () => {
      // Release handle to allow rescheduling
      this.smoothingTimer = null;
      updateDisplayIfNeeded();
    };

    const updateDisplayIfNeeded = () => {
      const scheduleCap = computeScheduleCap();
      const target = Math.min(realPercent, scheduleCap);

      if (target > displayedPercent) {
        displayedPercent = target;
        originalCallback(displayedPercent, lastLoaded, lastTotal);
      }

      // Stop when both real progress and schedule have reached 100%
      if (displayedPercent >= 100 && scheduleCap >= 100 && realPercent >= 100) {
        this.smoothingTimer = null;
        this.resolveSmoothingCompletion?.();
        this.resolveSmoothingCompletion = null;
        return;
      }

      // Ensure we keep ticking while schedule is catching up
      if (!this.smoothingTimer) {
        this.smoothingTimer = setTimeout(tick, updateInterval);
      }
    };

    // Ensure the schedule starts even if no progress events arrive immediately
    updateDisplayIfNeeded();

    // Provide a way to mark real completion explicitly
    this.smoothingMarkRealComplete = () => {
      if (realPercent < 100) {
        realPercent = 100;
        updateDisplayIfNeeded();
      }
    };

    return (percent: number, loaded: number, total: number) => {
      realPercent = Math.max(realPercent, Math.floor(percent));
      lastLoaded = loaded;
      lastTotal = total;
      updateDisplayIfNeeded();
    };
  }

  /**
   * Clears any active smoothing timers. Called during cancellation or cleanup.
   */
  private clearSmoothingTimer(): void {
    if (this.smoothingTimer) {
      clearTimeout(this.smoothingTimer);
      this.smoothingTimer = null;
    }
  }
}
